/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useMemo, useState } from 'react';

import {
  ILinkInstallment,
  PaymentMethods,
} from '@/pages/Checkout/types/paymentLinkContextTypes';
import { useParams } from 'react-router-dom';
import PaymentLinkContext from '@/pages/Checkout/context/PaymentLinkContext';
import { currencyFormater } from '@/utils/formatCurrency';
import useGooglePay from '@/hooks/googlePay';
import useApplePay from '@/hooks/useApplePay';

export const APPLE_PAY_MAX_INSTALLMENTS = 12;

export default function useInstallmentsDrawer(type: PaymentMethods) {
  const { id } = useParams();
  const { getSimulationData, submitCheckoutWallet } =
    useContext(PaymentLinkContext);

  const [installment, setInstallment] = useState<ILinkInstallment>(
    getSimulationData?.installments[0] || ({} as ILinkInstallment),
  );

  const showApplePayInstallmentsNotice =
    type === 'apple_pay' &&
    (getSimulationData?.installments.some(
      ({ number }) => number > APPLE_PAY_MAX_INSTALLMENTS,
    ) ??
      false);

  const handlePay = async () => {
    try {
      if (type === 'apple_pay') {
        // Apple Pay handles submitCheckoutWallet inside onpaymentauthorized
        await useApplePay({
          totalPrice: installment.total_amount,
          paymentLinkId: id as string,
          onPaymentAuthorized: async paymentData => {
            await submitCheckoutWallet(
              paymentData.token,
              type,
              installment.number,
            );
          },
        });
      } else if (type === 'google_pay') {
        // Google Pay returns payment data and submits afterwards
        const paymentData = await useGooglePay(installment.total_amount);

        if (!paymentData) {
          throw new Error('Payment data not received from Google Pay');
        }

        await submitCheckoutWallet(
          paymentData.paymentMethodData,
          type,
          installment.number,
        );
      } else {
        throw new Error(`Unsupported payment type: ${type}`);
      }
    } catch (error) {
      // TODO: add to sentry
      console.error('Error during payment:', error);
    }
  };

  const installmentsOptions = useMemo(() => {
    const options = [];

    if (getSimulationData) {
      const title = (installment: ILinkInstallment) => {
        if (getSimulationData.issuer_rate_forwarding) {
          return `${installment.number}x de ${currencyFormater(
            installment.amount,
          )} com juros`;
        }

        return `${installment.number}x sem juros`;
      };

      const description = (installment: ILinkInstallment) => {
        if (installment.number === 1) {
          return 'À vista';
        }

        if (getSimulationData.issuer_rate_forwarding) {
          return `Você paga ${currencyFormater(installment.total_amount)}`;
        }

        return `Parcelas de ${currencyFormater(installment.amount)}`;
      };

      const installments =
        type === 'apple_pay'
          ? getSimulationData.installments.filter(
              ({ number }) => number <= APPLE_PAY_MAX_INSTALLMENTS,
            )
          : getSimulationData.installments;

      for (const installment of installments) {
        const option = {
          title: title(installment),
          description: description(installment),
          value: installment.number,
          installment,
        };

        options.push(option);
      }
    }

    return options;
  }, [getSimulationData, type]);

  useEffect(() => {
    const isSelectionAvailable = installmentsOptions.some(
      option => option.installment.number === installment.number,
    );

    if (!isSelectionAvailable && installmentsOptions.length > 0) {
      setInstallment(installmentsOptions[0].installment);
    }
  }, [installmentsOptions, installment]);

  return {
    handlePay,
    installmentsOptions,
    installment,
    setInstallment,
    showApplePayInstallmentsNotice,
  };
}
